// c_zeta.js — ZETA検索API共通基盤。client-side fetchによる商品検索/サジェストの共通クライアントで、設定管理・エラー処理・モード切替（mock/local/production）を提供し window.Zeta を公開する。

(function (global) {
  'use strict';

  // モード判定: ?zeta_dev=1→'local'（assets配下の擬似JSON）/ ZETA_CONFIG.mode指定値 / 既定は'mock'。
  const detectMode = () => {
    const params = new URLSearchParams(global.location.search);
    if (params.get('zeta_dev') === '1') return 'local';
    const cfgMode = (global.ZETA_CONFIG || {}).mode;
    if (cfgMode === 'mock' || cfgMode === 'production' || cfgMode === 'local') return cfgMode;
    return 'mock';
  };

  // 設定値の解決（liquid出力のwindow.ZETA_CONFIG前提。未出力時はフェイルセーフのデフォルトを返す）。
  const resolveConfig = () => {
    const cfg = global.ZETA_CONFIG || {};
    return {
      mode: detectMode(),
      // CORS方針確定後にプロキシURL/JSONPへ切替可能なよう endpoints を分離（TODO: Zeta社確認後）。
      endpoints: {
        item: cfg.endpoints && cfg.endpoints.item,
        suggest: cfg.endpoints && cfg.endpoints.suggest,
        popular: cfg.endpoints && cfg.endpoints.popular
      },
      localEndpoints: {
        item: cfg.localEndpoints && cfg.localEndpoints.item,
        itemPatterns: (cfg.localEndpoints && cfg.localEndpoints.itemPatterns) || {},
        suggest: cfg.localEndpoints && cfg.localEndpoints.suggest
      },
      // 開発時は nl=1 でロギング抑止、本番のみ nl=0
      noLog: cfg.mode === 'production' ? 0 : 1,
      defaults: Object.assign(
        {
          limit: 60,
          offset: 0,
          sort: '-score'
        },
        cfg.defaults || {}
      )
    };
  };

  const config = resolveConfig();

  // クエリパラメータをキー昇順で正規化（HTTPキャッシュヒット率向上のため）。
  const buildQuery = (params) => {
    const cleaned = {};
    Object.keys(params || {}).forEach((k) => {
      const v = params[k];
      if (v === undefined || v === null || v === '') return;
      cleaned[k] = v;
    });
    const keys = Object.keys(cleaned).sort();
    return keys.map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(cleaned[k])}`).join('&');
  };

  // カスタムエラー。
  class ZetaError extends Error {
    constructor(message, { status, kind, cause } = {}) {
      super(message);
      this.name = 'ZetaError';
      this.status = status || 0;
      this.kind = kind || 'unknown'; // 'network' | 'http' | 'parse' | 'config' | 'unknown'
      this.cause = cause;
    }
  }

  // fetchラッパー: modeに応じURLを切替し、例外をZetaErrorに正規化（TODO: Zeta社確認後 — CORS方針確定後にJSONP/プロキシ分岐を追加）。
  const request = async (kind, params) => {
    const query = buildQuery(params);
    let url;

    switch (config.mode) {
      case 'local': {
        // ローカル擬似JSON（asset_urlで解決）。商品検索のみ ?zeta_dev_pattern=<key> でパターン切替（default/brand-filter/empty/1result）。
        let localUrl;
        if (kind === 'item') {
          const urlParams = new URLSearchParams(global.location.search);
          const pattern = urlParams.get('zeta_dev_pattern') || 'default';
          const patterns = config.localEndpoints.itemPatterns || {};
          localUrl = patterns[pattern] || patterns['default'] || config.localEndpoints.item;
        } else {
          localUrl = config.localEndpoints[kind];
        }
        if (!localUrl) throw new ZetaError(`local endpoint not configured: ${kind}`, { kind: 'config' });
        // 注: ローカル擬似JSONはパラメータを返却内容に反映しない（固定値）。クエリは付与のみ
        url = query ? `${localUrl}?${query}` : localUrl;
        break;
      }
      case 'mock':
      case 'production': {
        const endpoint = config.endpoints[kind];
        if (!endpoint) throw new ZetaError(`endpoint not configured: ${kind}`, { kind: 'config' });
        url = query ? `${endpoint}?${query}` : endpoint;
        break;
      }
      default:
        throw new ZetaError(`unknown mode: ${config.mode}`, { kind: 'config' });
    }

    let res;
    try {
      res = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
        headers: { Accept: 'application/json' }
      });
    } catch (e) {
      throw new ZetaError('network error', { kind: 'network', cause: e });
    }

    if (!res.ok) {
      throw new ZetaError(`http ${res.status}`, { kind: 'http', status: res.status });
    }

    try {
      return await res.json();
    } catch (e) {
      throw new ZetaError('json parse error', { kind: 'parse', cause: e });
    }
  };

  // 商品検索API（GET /api/item）。paramsは仕様書「リクエスト仕様」のクエリパラメータ。
  // ブランド名の表示用表記（キーは小文字）。データ側の値（絞り込み・連携に使う値）は変えず、画面に出す文字だけを置き換える。
  // 表記を追加・変更するときは snippets/c_brand-display-name.liquid と揃えること。
  const BRAND_DISPLAY_NAMES = {
    'st andrews': 'ST ANDREWS'
  };
  const displayBrandName = (name) => {
    if (name === undefined || name === null) return name;
    const source = String(name);
    const key = source.trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(BRAND_DISPLAY_NAMES, key) ? BRAND_DISPLAY_NAMES[key] : source;
  };
  // 文中に含まれるブランド名（店舗名「St ANDREWS 梅田阪急」など）を表示用表記へ置き換える。英数字に挟まれた部分一致は対象外。
  const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const displayBrandNameInText = (text) => {
    if (text === undefined || text === null) return text;
    let result = String(text);
    Object.keys(BRAND_DISPLAY_NAMES).forEach((key) => {
      const pattern = new RegExp(`(^|[^0-9A-Za-z])${escapeRegExp(key)}(?![0-9A-Za-z])`, 'gi');
      result = result.replace(pattern, (match, prefix) => prefix + BRAND_DISPLAY_NAMES[key]);
    });
    return result;
  };
  // ブランド系ファセットの表示名（name）だけを表示用表記へ置き換える。value は絞り込みに使うため変更しない。
  const BRAND_FACET_KEYS = ['brand', 'selectbrand'];
  const applyBrandDisplayNames = (data) => {
    const facet = data && data.result && data.result.facet;
    if (!facet) return data;
    BRAND_FACET_KEYS.forEach((key) => {
      if (!Array.isArray(facet[key])) return;
      facet[key].forEach((entry) => {
        if (!entry) return;
        const source = entry.name != null ? entry.name : entry.value;
        const display = displayBrandName(source);
        if (display !== String(source)) entry.name = display;
      });
    });
    return data;
  };

  const fetchItems = async (params) => {
    const merged = Object.assign(
      {
        l: config.defaults.limit,
        o: config.defaults.offset,
        s: config.defaults.sort,
        nl: config.noLog
      },
      params || {}
    );
    return applyBrandDisplayNames(await request('item', merged));
  };

  // サジェストAPI（GET /api/suggest）。params: { q_word, keyword_limit, brand_limit, product_limit }。
  const fetchSuggest = async (params) => {
    const merged = Object.assign(
      { keyword_limit: 10, brand_limit: 10, product_limit: 10 },
      params || {}
    );
    return request('suggest', merged);
  };

  // items[].id のゼロパディング暫定処理（9桁時のみ先頭"0"付与で10桁化。TODO: Zeta社確認後 — 先頭ゼロ落ちしない保証が取れたら不要）。
  const normalizeItemId = (id) => {
    if (id === null || id === undefined) return '';
    const s = String(id);
    if (s.length === 9) return `0${s}`;
    return s;
  };

  // 表示用タグのホワイトリスト抽出: tagsには管理タグ（CreatedAt:... 等）が大量混在するため、フロント表示は販売タイプ系のみ許可。
  const DISPLAY_TAG_WHITELIST = new Set(['NEW', 'Pre-order', 'Web-limited', 'Restock']);

  const extractDisplayTags = (tags) => {
    if (!Array.isArray(tags)) return [];
    return tags.filter((t) => DISPLAY_TAG_WHITELIST.has(t));
  };

  // calloutメタオブジェクト由来のバッジ定義。c_zeta-config.liquidが出力するwindow.ZETA_CALLOUTSを正とし、タグ名・文言・色をコード側に持たない。
  const getCallouts = () => (Array.isArray(global.ZETA_CALLOUTS) ? global.ZETA_CALLOUTS : []);

  // 全商品対象を示すweb_tagの予約語（Shopify管理画面の入力値。config側でdowncase済み）。
  const CALLOUT_ALL_ITEMS_TAG = 'allitems';

  // 掲載期間内かつタグ一致のcalloutを抽出。期間判定はクライアント時刻（現行のc_timerと同方式）。
  const matchCallouts = (tags) => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const lowerTags = (Array.isArray(tags) ? tags : []).map((tag) => String(tag).toLowerCase());
    return getCallouts().filter((callout) => {
      if (!callout || !callout.tag || !callout.title) return false;
      if (callout.startsAt !== null && callout.startsAt !== undefined && nowSeconds < Number(callout.startsAt)) return false;
      if (callout.endsAt !== null && callout.endsAt !== undefined && nowSeconds > Number(callout.endsAt)) return false;
      return callout.tag === CALLOUT_ALL_ITEMS_TAG || lowerTags.includes(callout.tag);
    });
  };

  // calloutメタオブジェクト未登録の単発施策バッジ。c_product-item.liquidの直書き分の移植で、callout側へ集約でき次第この配列は空にできる。
  const LEGACY_TAG_LABELS = [
    { prefix: '25fw-presicon-', buildText: (rate) => `プレセール対象${rate}%OFF`, backgroundColor: '#ae5581' },
    { prefix: 'us-saleicon-', buildText: (rate) => `限定セール${rate}%オフ`, backgroundColor: '#dab710' },
    { exact: 'mix-250808-2buy10', buildText: () => '2BUY10％OFF対象', backgroundColor: '#0067c0' },
    { exact: 'mix-250815-more10', buildText: () => 'MORE10％OFF対象', backgroundColor: '#0067c0' },
    { exact: 'mix-ecp-pre', buildText: () => '予約会対象品', backgroundColor: '#069d61' },
    { exact: 'mix-cpicon-0415', buildText: () => '24時間限定MORE10%OFF', backgroundColor: '#00c98c' }
  ];

  // 直書き施策タグに一致するバッジを抽出。prefix指定は末尾の割引率を文言へ差し込む。
  const matchLegacyLabels = (tags) => {
    const matched = [];
    (Array.isArray(tags) ? tags : []).forEach((rawTag) => {
      const tag = String(rawTag).toLowerCase();
      LEGACY_TAG_LABELS.forEach((definition) => {
        if (definition.exact && tag === definition.exact) {
          matched.push({ tag, title: definition.buildText(''), backgroundColor: definition.backgroundColor, textColor: '' });
          return;
        }
        if (definition.prefix && tag.indexOf(definition.prefix) === 0) {
          const rate = tag.slice(definition.prefix.length);
          if (rate) matched.push({ tag, title: definition.buildText(rate), backgroundColor: definition.backgroundColor, textColor: '' });
        }
      });
    });
    return matched;
  };

  // 価格フォーマット（¥1,234）。TODO: Zeta社確認後 — themeVariables.settings.moneyFormat を使う形に置換。
  const formatPrice = (n) => {
    if (n === null || n === undefined) return '';
    return `¥${Number(n).toLocaleString('ja-JP')}`;
  };

  // 消費税率10%。ZETA APIの価格は税抜（MIXTOKYO-41）のため、表示は税込へ・絞り込みリクエストは税抜へ変換する。
  const TAX_RATE_PERCENT = 10;
  const toTaxIncludedPrice = (price) => Math.round((price * (100 + TAX_RATE_PERCENT)) / 100);
  const toTaxExcludedFloor = (price) => Math.floor((price * 100) / (100 + TAX_RATE_PERCENT));
  const toTaxExcludedCeil = (price) => Math.ceil((price * 100) / (100 + TAX_RATE_PERCENT));

  // 販売タイプタグ→バッジ表示の対応（仕様書「販売タイプ抽出条件」＋テーマのlabelクラス準拠。TODO: 正式タグ値・表記はTSI確定待ち#14）。
  const LABEL_DEFS = {
    NEW: { text: 'NEW', cls: 'label--custom is-new' },
    'Pre-order': { text: 'PRE-ORDER', cls: 'label--pre-order' },
    'Web-limited': { text: 'WEB限定', cls: 'label--custom' },
    Restock: { text: '再入荷', cls: 'label--custom' }
  };
  const DISCOUNT_LABEL_CLASS = 'label label--highlight c_sale-label';

  // 実APIはセール無しでもsale_price:0を返すため、正の値かつregular_price未満のときだけ割引とみなす。
  const calcDiscountPercent = (variant) => {
    if (!variant) return null;
    const regularPrice = Number(variant.regular_price);
    const salePrice = Number(variant.sale_price);
    if (!(regularPrice > 0) || !(salePrice > 0) || salePrice >= regularPrice) return null;
    const percent = Math.round(((regularPrice - salePrice) / regularPrice) * 100);
    return percent > 0 ? percent : null;
  };

  // 画像の表示サイズ最適化。ZETAはリサイズ前のURLを返す（実測1280x1536・約130KB）ため、
  // 50件のカードを原寸で読むとデコード後のビットマップが1枚あたり約7.5MiB・1ページで1GiB超になる。
  // iOS Safariは上限を超えた分を破棄するので、上方向へ戻したときに再デコードが走りスクロールが引っかかる。
  // 候補幅の上限は800px。PCの最大カード幅が425px程度で、1200pxは元画像より重くなる（実測200KB > 132KB）ため入れない。
  const CARD_IMAGE_SRCSET_WIDTHS = [200, 300, 400, 600, 800]; // カード画像の候補幅（実際の選択はテンプレートのsizes属性）
  const CARD_IMAGE_FALLBACK_WIDTH = 400; // srcset非対応環境向けのsrc
  const SWATCH_IMAGE_WIDTH = 100; // カラースウォッチは33px表示なので3倍で足りる

  // Shopify CDN以外（ZETAが別ドメインの画像を返す場合）はwidthパラメータが効かないため加工しない。
  const isResizableImageUrl = (rawUrl) => {
    if (!rawUrl) return false;
    try {
      const url = new URL(rawUrl, global.location.href);
      return url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.cdn.shopify.com') || url.pathname.indexOf('/cdn/shop/') === 0;
    } catch (_) {
      return false;
    }
  };

  const withImageWidth = (rawUrl, width) => {
    if (!isResizableImageUrl(rawUrl)) return rawUrl || '';
    const url = new URL(rawUrl, global.location.href);
    url.searchParams.set('width', String(width));
    return url.toString();
  };

  const buildImageSrcset = (rawUrl, widths) => {
    if (!isResizableImageUrl(rawUrl)) return '';
    return widths.map((width) => `${withImageWidth(rawUrl, width)} ${width}w`).join(', ');
  };

  // 商品カードDOMを生成（検索結果・特集コレクションで共用）。既存テーマのc_product-itemと同じクラス構成に値を流し込み本番デザインを踏襲。テンプレ不正時はnull。
  const buildProductCard = (item, templateEl, opts) => {
    if (!item || !templateEl || !templateEl.content || !templateEl.content.firstElementChild) return null;
    const node = templateEl.content.firstElementChild.cloneNode(true);

    const handle = normalizeItemId(item.id);
    const url = `/products/${handle}`;
    const buildCardHref = (variant) => (variant && variant.id ? `${url}?zeta_sku=${encodeURIComponent(variant.id)}` : url);
    node.dataset.card = handle;

    const variants = Array.isArray(item.variants) ? item.variants : [];
    const selectedColors = (opts && opts.selectedColors) || null;
    // カラー絞り込みで色が特定できたときだけ、その variant を画像・リンク・スウォッチ選択枠の基準にする
    let matchedVariantIndex = -1;
    if (selectedColors && selectedColors.size) {
      matchedVariantIndex = variants.findIndex((variant) => variant && selectedColors.has(variant.color));
    }
    const hasColorSelection = matchedVariantIndex >= 0;
    const currentVariantIndex = hasColorSelection ? matchedVariantIndex : 0;
    const currentVariant = variants[currentVariantIndex] || null;
    node.querySelectorAll('[data-card-href]').forEach((el) => el.setAttribute('href', hasColorSelection ? buildCardHref(currentVariant) : url));

    const imgEl = node.querySelector('[data-card-image]');
    // メイン画像の差し込み（初期表示とスウォッチ切替で共用）。sizesはテンプレート側に静的に持たせている。
    const applyCardImage = (rawUrl) => {
      if (!imgEl || !rawUrl) return;
      const srcset = buildImageSrcset(rawUrl, CARD_IMAGE_SRCSET_WIDTHS);
      if (srcset) {
        imgEl.srcset = srcset;
      } else {
        imgEl.removeAttribute('srcset');
      }
      imgEl.src = withImageWidth(rawUrl, CARD_IMAGE_FALLBACK_WIDTH);
    };
    const mainImage = (hasColorSelection && currentVariant && currentVariant.image_url) || item.image_url || (currentVariant && currentVariant.image_url) || '';
    if (imgEl && mainImage) {
      applyCardImage(mainImage);
      imgEl.alt = item.item_name || '';
    }

    const brandEl = node.querySelector('[data-card-brand]');
    if (brandEl) brandEl.textContent = displayBrandName(item.brand_name || '');
    const titleEl = node.querySelector('[data-card-title]');
    if (titleEl) titleEl.textContent = item.item_name || '';

    // バッジ（callout メタオブジェクト -> 直書き施策タグ -> 販売タイプ系 の順。現行テーマ c_product-item.liquid と同じ並び）
    const labelWrap = node.querySelector('[data-card-labels]');
    const showDiscount = !(opts && opts.showDiscount === false);
    let discountLabel = null;
    if (labelWrap) {
      const appendCustomLabel = (definition) => {
        const span = document.createElement('span');
        span.className = `label label--custom is-${definition.tag}`;
        if (definition.backgroundColor) {
          span.style.backgroundColor = definition.backgroundColor;
          span.style.border = `1px solid ${definition.backgroundColor}`;
        }
        if (definition.textColor) span.style.color = definition.textColor;
        span.textContent = definition.title;
        labelWrap.appendChild(span);
      };

      // calloutに登録済みのタグは直書き側をスキップする。同じタグをcalloutへ集約した時点で二重描画になるのを防ぐ。
      const calloutLabels = matchCallouts(item.tags);
      const calloutTags = new Set(calloutLabels.map((callout) => callout.tag));
      calloutLabels.forEach(appendCustomLabel);
      matchLegacyLabels(item.tags)
        .filter((legacyLabel) => !calloutTags.has(legacyLabel.tag))
        .forEach(appendCustomLabel);

      // SALEとセールのように別タグが同一表記へ解決されるため、表示テキスト単位で重複を除く
      const renderedDisplayLabels = new Set();
      extractDisplayTags(item.tags).forEach((displayTag) => {
        const definition = LABEL_DEFS[displayTag];
        const labelText = definition ? definition.text : displayTag;
        if (renderedDisplayLabels.has(labelText)) return;
        renderedDisplayLabels.add(labelText);
        const span = document.createElement('span');
        span.className = `label ${definition ? definition.cls : 'label--custom'}`;
        span.textContent = labelText;
        labelWrap.appendChild(span);
      });

      if (showDiscount) {
        discountLabel = document.createElement('span');
        discountLabel.className = DISCOUNT_LABEL_CLASS;
        discountLabel.hidden = true;
        labelWrap.appendChild(discountLabel);
      }
    }

    const updateLabelVisibility = () => {
      if (!labelWrap) return;
      const visibleCount = Array.prototype.filter.call(labelWrap.children, (element) => !element.hidden).length;
      labelWrap.hidden = visibleCount === 0;
    };

    const applyDiscountLabel = (variant) => {
      if (!discountLabel) return;
      const percent = calcDiscountPercent(variant);
      if (percent === null) {
        discountLabel.hidden = true;
        discountLabel.textContent = '';
      } else {
        discountLabel.hidden = false;
        discountLabel.textContent = `${percent}%OFF`;
      }
      updateLabelVisibility();
    };
    updateLabelVisibility();

    // 価格（カレント variant 基準）
    const priceEl = node.querySelector('[data-card-price]');
    const applyPrice = (v) => {
      if (!v) return;
      applyDiscountLabel(v);
      if (!priceEl) return;
      const onSale = calcDiscountPercent(v) !== null;
      const value = onSale ? v.sale_price : v.regular_price;
      priceEl.textContent = formatPrice(toTaxIncludedPrice(value));
      const taxEl = node.querySelector('[data-card-price-tax]');
      if (taxEl) taxEl.textContent = '（税込）';
      priceEl.classList.toggle('price--highlight', Boolean(onSale));
    };
    applyPrice(currentVariant);

    // カラースウォッチ（variants[].image_url の小サムネ。クリックでメイン画像・価格を切替）
    const swatchWrap = node.querySelector('[data-card-swatches]');
    const showSwatch = !(opts && opts.showSwatch === false);
    if (swatchWrap && showSwatch && variants.length > 1) {
      variants.forEach((v, i) => {
        if (!v.image_url) return;
        const sw = document.createElement('div');
        sw.className = 'color-swatch';
        if (hasColorSelection && i === currentVariantIndex) sw.classList.add('is-selected');

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'color-swatch__item c_zeta-card__swatch';
        btn.setAttribute('aria-label', v.color || '');

        const simg = document.createElement('img');
        simg.src = withImageWidth(v.image_url, SWATCH_IMAGE_WIDTH);
        simg.width = 33;
        simg.height = 33;
        simg.alt = v.color || '';
        simg.loading = 'lazy';
        simg.decoding = 'async';
        btn.appendChild(simg);

        btn.addEventListener('click', () => {
          applyCardImage(v.image_url);
          applyPrice(v);
          node.querySelectorAll('[data-card-href]').forEach((el) => el.setAttribute('href', buildCardHref(v)));
          swatchWrap.querySelectorAll('.color-swatch').forEach((s) => s.classList.remove('is-selected'));
          sw.classList.add('is-selected');
        });

        sw.appendChild(btn);
        swatchWrap.appendChild(sw);
      });
      if (swatchWrap.children.length) swatchWrap.hidden = false;
    }

    // TODO: Zeta社確認後 — related_sku（同色他サイズ）の表示仕様が決まったらここで実装
    return node;
  };

  const LEGACY_PARAMETER_PREFIX = 'pf_';
  const LEGACY_PARAMETER_MAP = {
    pf_v_burando: 'brand',
    pf_mlt_kategori: 'category',
    'pf_m_::custom::product-gender': 'gender',
    pf_t_fan_mai_taipu: 'saletype'
  };
  const LEGACY_STOCK_PARAMETER = 'pf_st_zai_ku_zhuang_kuang';
  const LEGACY_STOCK_VALUE_MAP = {
    'in-stock': '1',
    'out-of-stock': '0'
  };
  const ZETA_STOCK_PARAMETER = 'stock_flg';
  const LEGACY_PRICE_PARAMETER = 'pf_p_jia_ge';
  const ZETA_PRICE_FROM_PARAMETER = 'price__from';
  const ZETA_PRICE_TO_PARAMETER = 'price__to';
  const LEGACY_SORT_MAP = {
    'best-selling': '-sales',
    manual: 'collectionorder',
    'extra-sort1-descending': null
  };
  const LEGACY_SORT_PARAMETER = 'sort';
  const ZETA_SORT_PARAMETER = 's';

  // 旧パラメータの値を空白・重複なしで集約する。
  const getUniqueLegacyValues = (parameters, key) => [
    ...new Set(parameters.getAll(key).map((value) => value.trim()).filter(Boolean))
  ];

  // Boost形式の絞り込みクエリをZETA形式へ置換する。
  const migrateLegacyZetaParameters = () => {
    const sourceParameters = new URLSearchParams(global.location.search);
    const hasLegacyFilter = Array.from(sourceParameters.keys()).some((key) =>
      key.startsWith(LEGACY_PARAMETER_PREFIX)
    );
    const legacySortValue = sourceParameters
      .getAll(LEGACY_SORT_PARAMETER)
      .find((value) => Object.prototype.hasOwnProperty.call(LEGACY_SORT_MAP, value));
    if (!hasLegacyFilter && legacySortValue === undefined) return;

    const migratedParameters = new URLSearchParams();
    const processedLegacyKeys = new Set();
    const droppedKeys = [];

    sourceParameters.forEach((value, key) => {
      if (key === LEGACY_SORT_PARAMETER && legacySortValue !== undefined) {
        if (processedLegacyKeys.has(key)) return;
        processedLegacyKeys.add(key);
        if (LEGACY_SORT_MAP[legacySortValue] === null) {
          droppedKeys.push(`${key}=${legacySortValue}`);
        } else if (sourceParameters.has(ZETA_SORT_PARAMETER)) {
          droppedKeys.push(key);
        } else {
          migratedParameters.append(ZETA_SORT_PARAMETER, LEGACY_SORT_MAP[legacySortValue]);
        }
        return;
      }

      if (!key.startsWith(LEGACY_PARAMETER_PREFIX)) {
        migratedParameters.append(key, value);
        return;
      }
      if (processedLegacyKeys.has(key)) return;
      processedLegacyKeys.add(key);

      const targetParameter = LEGACY_PARAMETER_MAP[key];
      if (targetParameter) {
        if (sourceParameters.has(targetParameter)) {
          droppedKeys.push(key);
        } else {
          const values = getUniqueLegacyValues(sourceParameters, key);
          if (values.length) migratedParameters.append(targetParameter, values.join(','));
        }
        return;
      }

      if (key === LEGACY_STOCK_PARAMETER) {
        if (sourceParameters.has(ZETA_STOCK_PARAMETER)) {
          droppedKeys.push(key);
        } else {
          const stockValues = getUniqueLegacyValues(sourceParameters, key)
            .map((stockValue) => LEGACY_STOCK_VALUE_MAP[stockValue])
            .filter((stockValue) => stockValue !== undefined);
          if (stockValues.length) migratedParameters.append(ZETA_STOCK_PARAMETER, [...new Set(stockValues)].join(','));
        }
        return;
      }

      if (key === LEGACY_PRICE_PARAMETER) {
        const priceRange = getUniqueLegacyValues(sourceParameters, key)[0] || '';
        const [rawPriceFrom, rawPriceTo] = priceRange.split(':');
        const priceFrom = Number(rawPriceFrom);
        const priceTo = Number(rawPriceTo);
        const hasZetaPriceFrom = sourceParameters.has(ZETA_PRICE_FROM_PARAMETER);
        const hasZetaPriceTo = sourceParameters.has(ZETA_PRICE_TO_PARAMETER);
        if (hasZetaPriceFrom && hasZetaPriceTo) droppedKeys.push(key);
        if (!hasZetaPriceFrom && priceFrom > 0) {
          migratedParameters.append(ZETA_PRICE_FROM_PARAMETER, String(toTaxIncludedPrice(priceFrom)));
        }
        if (!hasZetaPriceTo && priceTo > 0) {
          migratedParameters.append(ZETA_PRICE_TO_PARAMETER, String(toTaxIncludedPrice(priceTo)));
        }
        return;
      }

      droppedKeys.push(key);
    });

    const from = global.location.search.slice(1);
    const to = migratedParameters.toString();
    const migratedUrl = `${global.location.pathname}${to ? `?${to}` : ''}${global.location.hash}`;
    global.history.replaceState(null, '', migratedUrl);
    global.ZETA_LEGACY_MIGRATION = { from, to, dropped: droppedKeys };
  };

  migrateLegacyZetaParameters();

  global.Zeta = {
    config,
    mode: config.mode,
    ZetaError,
    fetchItems,
    fetchSuggest,
    normalizeItemId,
    extractDisplayTags,
    formatPrice,
    toTaxIncludedPrice,
    toTaxExcludedFloor,
    toTaxExcludedCeil,
    buildProductCard,
    buildQuery,
    displayBrandName,
    displayBrandNameInText
  };
})(window);
